import { useEffect, useState, useCallback } from 'react';
import Modal from '../Modal';
import apiService from '../../services/api';
import { createCaisseIdempotencyKey } from '../../utils/caisseIdempotency';
import { getRdvPaymentView } from '../../utils/rdvPaymentDisplay';

const METHODS = ['ESPECES', 'CARTE', 'CHEQUE', 'VIREMENT'];

const IDEM_STORAGE_PREFIX = 'rdv-terminer:';

function idemStorageKey(rdvId, encaisserSolde) {
  return `${IDEM_STORAGE_PREFIX}${rdvId}:${encaisserSolde ? '1' : '0'}`;
}

function readOrCreateIdemKey(rdvId, encaisserSolde) {
  const key = idemStorageKey(rdvId, encaisserSolde);
  try {
    const existing = sessionStorage.getItem(key);
    if (existing && existing.length >= 8) return existing;
    const fresh = createCaisseIdempotencyKey();
    sessionStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return createCaisseIdempotencyKey();
  }
}

function clearIdemKey(rdvId) {
  try {
    sessionStorage.removeItem(idemStorageKey(rdvId, false));
    sessionStorage.removeItem(idemStorageKey(rdvId, true));
  } catch {
    /* ignore */
  }
}

/**
 * Slice 2.2 — Terminer RDV (seul ou + encaisser solde via caisse POS si module actif).
 * RDV ≠ POS : sans moduleCaisse, pas d’exigence de session caisse.
 */
export default function RdvTerminerModal({
  open,
  commande,
  commercantId,
  onClose,
  onSuccess,
  formatEuro,
}) {
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [moduleCaisse, setModuleCaisse] = useState(false);
  const [methodePaiement, setMethodePaiement] = useState('ESPECES');
  const [error, setError] = useState('');

  const rdvPay = commande ? getRdvPaymentView(commande) : null;
  const due = Number(rdvPay?.amountDueLater || 0);
  const total = Number(rdvPay?.total || 0);
  const paid = Number(rdvPay?.amountPaid || 0);
  const duePositive = due > 0.001;
  const posEnabled = moduleCaisse === true;

  const probeModuleAndSession = useCallback(async () => {
    if (!commercantId) {
      setModuleCaisse(false);
      setHasSession(false);
      return;
    }
    setModuleLoading(true);
    setSessionLoading(true);
    let enabled = false;
    try {
      const commercant = await apiService.getCommercant(commercantId);
      enabled = Boolean(commercant?.moduleCaisse);
      setModuleCaisse(enabled);
    } catch {
      // Fail-closed POS : sans preuve module, ne pas forcer la caisse
      enabled = false;
      setModuleCaisse(false);
    } finally {
      setModuleLoading(false);
    }

    if (!enabled) {
      setHasSession(false);
      setSessionLoading(false);
      return;
    }

    try {
      const data = await apiService.getCaisseSession(commercantId);
      setHasSession(Boolean(data?.session?.id));
    } catch {
      setHasSession(false);
    } finally {
      setSessionLoading(false);
    }
  }, [commercantId]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setMethodePaiement('ESPECES');
    setLoading(false);
    probeModuleAndSession();
  }, [open, commande?.id, probeModuleAndSession]);

  if (!open || !commande) return null;

  const runTerminer = async (encaisserSolde) => {
    setError('');
    setLoading(true);
    try {
      if (encaisserSolde && !posEnabled) {
        setError('Encaissement caisse indisponible : le module caisse n’est pas activé.');
        setLoading(false);
        return;
      }
      const body = {
        encaisserSolde: Boolean(encaisserSolde),
        ...(encaisserSolde ? { methodePaiement } : {}),
      };
      const opts = {
        idempotencyKey: readOrCreateIdemKey(commande.id, Boolean(encaisserSolde)),
      };

      await apiService.terminerRendezVous(commande.id, body, opts);
      clearIdemKey(commande.id);
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      const msg =
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.message ||
        'Impossible de terminer le rendez-vous';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const probing = moduleLoading || sessionLoading;

  return (
    <Modal
      isOpen={open}
      onClose={loading ? undefined : onClose}
      title="Terminer le rendez-vous"
      maxWidth="max-w-md"
    >
      <div className="space-y-4">
        {rdvPay ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-1">
            <p>
              Total : <span className="font-medium">{formatEuro(total)}</span>
            </p>
            <p>
              Déjà payé : <span className="font-medium">{formatEuro(paid)}</span>
            </p>
            <p>
              Solde : <span className="font-medium">{formatEuro(due)}</span>
            </p>
          </div>
        ) : null}

        {error ? (
          <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
        ) : null}

        {!duePositive ? (
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              Annuler
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => runTerminer(false)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              {loading ? '…' : 'Terminer'}
            </button>
          </div>
        ) : !posEnabled && !probing ? (
          <>
            {duePositive ? (
              <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                Solde restant à encaisser hors caisse (module caisse non activé pour ce
                commerce). Vous pouvez terminer le rendez-vous.
              </p>
            ) : null}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled={loading}
                onClick={() => runTerminer(false)}
                className="w-full px-4 py-2.5 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {loading ? '…' : 'Terminer'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                Annuler
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Méthode (si encaissement)
              </label>
              <select
                value={methodePaiement}
                onChange={(e) => {
                  setMethodePaiement(e.target.value);
                  clearIdemKey(commande.id);
                }}
                disabled={loading || !hasSession}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {!probing && !hasSession ? (
              <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
                Ouvrez la caisse pour pouvoir encaisser le solde. Vous pouvez
                tout de même terminer sans encaisser.
              </p>
            ) : null}

            <div className="flex flex-col gap-2">
              <button
                type="button"
                disabled={loading}
                onClick={() => runTerminer(false)}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Terminer seulement
              </button>
              <button
                type="button"
                disabled={loading || !hasSession || probing}
                onClick={() => runTerminer(true)}
                className="w-full px-4 py-2.5 text-sm font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? '…'
                  : `Terminer + encaisser ${formatEuro(due)}`}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
